import { Compass, Home, Inbox, Plus, User, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { JemmiLogo } from '@/components/JemmiLogo';
import { TestSessionBadge } from '@/components/TestSessionBadge';
import { useNotifications } from '@/hooks/useNotifications';
import { cn } from '@/lib/cn';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
};

/**
 * Single source of truth for the nav. Sidebar and BottomNav both render from
 * this list — change a label or icon once, and both layouts update.
 * `badge` is filled in at render time from useNotifications().unreadCount.
 */
const BASE_NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/explore', label: 'Explore', icon: Compass },
  { to: '/notifications', label: 'Notifications', icon: Inbox },
  { to: '/profile', label: 'Profile', icon: User },
];

/** Builds NAV_ITEMS with the live unread count applied to Notifications. */
function useNavItems(): readonly NavItem[] {
  const { unreadCount } = useNotifications();
  return BASE_NAV_ITEMS.map((item) =>
    item.to === '/notifications' && unreadCount > 0 ? { ...item, badge: unreadCount } : item,
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Sidebar (desktop)
// ──────────────────────────────────────────────────────────────────────────

export function Sidebar() {
  const navItems = useNavItems();
  return (
    <aside className="hidden w-60 shrink-0 flex-col pr-6 lg:flex">
      <div className="mb-8 px-3">
        <JemmiLogo size="md" />
      </div>
      <nav className="flex flex-col gap-1">
        {navItems.map(({ to, label, icon: Icon, badge }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors',
                isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-100',
              )
            }
          >
            <Icon className="h-5 w-5" />
            <span className="flex-1 text-left">{label}</span>
            {badge ? (
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                {badge}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>
      <NavLink
        to="/create"
        className="from-brand-500 to-brand-700 shadow-brand-md hover:shadow-brand-lg mx-2 mt-6 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-br px-4 py-3 text-sm font-semibold text-white transition-all active:scale-[0.98]"
      >
        <Plus className="h-4 w-4" strokeWidth={2.5} />
        Create challenge
      </NavLink>

      <div className="mt-6 px-2">
        <TestSessionBadge variant="compact" />
      </div>
    </aside>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// BottomNav (mobile)
// ──────────────────────────────────────────────────────────────────────────

export function BottomNav() {
  const navItems = useNavItems();
  return (
    <nav className="safe-bottom fixed inset-x-0 bottom-0 border-t border-slate-200/80 bg-white/90 backdrop-blur-xl lg:hidden">
      <div className="mx-auto flex max-w-md items-end justify-around px-2 py-2">
        <BottomNavItem item={navItems[0]!} />
        <BottomNavItem item={navItems[1]!} />
        <CreateButton />
        <BottomNavItem item={navItems[2]!} />
        <BottomNavItem item={navItems[3]!} />
      </div>
    </nav>
  );
}

function BottomNavItem({ item }: { item: NavItem }) {
  const { to, label, icon: Icon, badge } = item;
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'relative flex flex-col items-center gap-0.5 px-3 py-1 transition-colors',
          isActive ? 'text-brand-600' : 'text-slate-500',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="h-5 w-5" strokeWidth={isActive ? 2.5 : 2} />
          <span className="text-[10px] font-medium">{label}</span>
          {badge ? (
            <span className="absolute right-2 top-0 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

function CreateButton() {
  return (
    <NavLink
      to="/create"
      aria-label="Create challenge"
      className="from-brand-500 to-brand-700 shadow-brand-lg relative -mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br text-white transition-transform active:scale-95"
    >
      <Plus className="h-7 w-7" strokeWidth={2.5} />
    </NavLink>
  );
}
